import React, { useEffect, useState, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Camera,
  Trash2,
  FileText,
  FileDown
} from 'lucide-react'
import { generateDocumentReactPdf, triggerDownload } from '../services/reactPdfService.jsx'
import { surveyApi } from '../services/apiService'
import { Button, Card, Modal, ConfirmModal } from '../components/common'
import {
  CategorySelector,
  CameraCapture,
  PhotoGallery,
  AnalyseButton
} from '../components/survey'
import useSurveyStore from '../stores/surveyStore'
import useCategoryStore from '../stores/categoryStore'
import { calculateSurveyProgress } from '../utils/helpers'
import useMobileLayout from '../hooks/useMobileLayout'
import MobileSurveyPage from '../components/mobile/MobileSurveyPage'

export default function SurveyPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const isMobile = useMobileLayout()

  const [selectedCategory, setSelectedCategory] = useState(null)
  const [showCamera, setShowCamera] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [showCompleteConfirm, setShowCompleteConfirm] = useState(false)
  const [downloading, setDownloading] = useState(false)

  const currentSurvey = useSurveyStore(state => state.currentSurvey)
  const currentPhotos = useSurveyStore(state => state.currentPhotos)
  const currentAiResponses = useSurveyStore(state => state.currentAiResponses)
  const loading = useSurveyStore(state => state.loading)
  const loadSurvey = useSurveyStore(state => state.loadSurvey)
  const deleteSurvey = useSurveyStore(state => state.deleteSurvey)
  const addPhoto = useSurveyStore(state => state.addPhoto)
  const deletePhoto = useSurveyStore(state => state.deletePhoto)
  const updatePhotoNotes = useSurveyStore(state => state.updatePhotoNotes)
  const togglePhotoAnalyse = useSurveyStore(state => state.togglePhotoAnalyse)
  const completeSurvey = useSurveyStore(state => state.completeSurvey)
  const clearCurrentSurvey = useSurveyStore(state => state.clearCurrentSurvey)

  const categories = useCategoryStore(state => state.getActiveCategories())
  const loadCategories = useCategoryStore(state => state.loadCategories)

  useEffect(() => {
    if (id) {
      loadSurvey(id)
      loadCategories()
    }

    return () => {
      clearCurrentSurvey()
    }
  }, [id, loadSurvey, loadCategories, clearCurrentSurvey])

  // Mobile layout
  if (isMobile) {
    return <MobileSurveyPage surveyId={id} />
  }

  const handleCapture = useCallback(async (photoData) => {
    if (!selectedCategory) return

    await addPhoto({
      ...photoData,
      categoryCode: selectedCategory.code
    })
    setShowCamera(false)
  }, [selectedCategory, addPhoto])

  const handleDeleteSurvey = async () => {
    await deleteSurvey(id)
    navigate('/surveys')
  }

  const handleCompleteSurvey = async () => {
    await completeSurvey()
    setShowCompleteConfirm(false)
  }

  const handleAnalysisComplete = (results) => {
    navigate(`/survey/${id}/analysis`)
  }

  const handleReanalyseCategory = useCallback(async (categoryCode) => {
    const result = await surveyApi.analyseCategory(currentSurvey.id, categoryCode)
    // Reload survey to get updated analysis results
    await loadSurvey(currentSurvey.id)
    return result
  }, [currentSurvey?.id, loadSurvey])

  const handleDownloadDocument = async () => {
    if (!currentSurvey) return

    setDownloading(true)
    try {
      // Start with AI responses as baseline
      const aiDescriptions = {}
      const categoryRatings = {}
      const limitations = {}

      // Sort by createdAt descending to get most recent first
      const sortedResponses = [...currentAiResponses]
        .filter(r => r.success)
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))

      // Take most recent response per category as baseline
      for (const response of sortedResponses) {
        if (!aiDescriptions[response.categoryCode] && response.response) {
          aiDescriptions[response.categoryCode] = response.response
        }
      }

      // Override with saved reviewData if it exists (user edits from Review & Finalize)
      if (currentSurvey.reviewData) {
        for (const [code, data] of Object.entries(currentSurvey.reviewData)) {
          if (code.includes('_limitations')) {
            // Limitations text
            if (data.comment) limitations[code] = data.comment
          } else {
            // Category comment and rating
            if (data.comment) aiDescriptions[code] = data.comment
            if (data.rating) categoryRatings[code] = data.rating
          }
        }
      }

      console.log('PDF data - descriptions:', Object.keys(aiDescriptions).length, 'ratings:', Object.keys(categoryRatings).length)

      // Generate document with all data including ratings and limitations
      const blob = await generateDocumentReactPdf(currentSurvey, currentPhotos, aiDescriptions, categoryRatings, limitations)
      const filename = `${currentSurvey.propertyName.replace(/[^a-z0-9]/gi, '_')}_survey.pdf`
      triggerDownload(blob, filename)
    } catch (error) {
      console.error('Failed to download document:', error)
      alert('Failed to download document. Please try again.')
    } finally {
      setDownloading(false)
    }
  }

  if (!currentSurvey && !loading) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-slate-900 mb-2">Survey not found</h2>
        <p className="text-slate-500 mb-4">The survey you're looking for doesn't exist.</p>
        <Button variant="primary" onClick={() => navigate('/surveys')}>
          View All Surveys
        </Button>
      </div>
    )
  }

  if (loading || !currentSurvey) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="spinner w-8 h-8" />
      </div>
    )
  }

  const progress = calculateSurveyProgress(currentPhotos, categories)

  return (
    <div className="h-full flex flex-col -m-4 lg:-m-6 p-4 lg:p-6 overflow-hidden">
      {/* Compact progress bar */}
      <div className="flex-shrink-0 mb-4">
        <div className="flex items-center gap-4 text-sm">
          <div className="flex-1">
            <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-primary-600 transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
          <span className="text-slate-600 whitespace-nowrap">
            {progress}% | {currentPhotos.length} photos | {new Set(currentPhotos.map(p => p.categoryCode)).size} categories
          </span>
        </div>
      </div>

      {/* Main content grid - fills remaining space */}
      <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Left column - Camera and categories */}
        <div className="lg:col-span-1 space-y-4 overflow-y-auto">
          {/* Category selection */}
          <Card>
            <h3 className="font-semibold text-slate-900 mb-4">Select Category</h3>
            <CategorySelector
              selectedCategory={selectedCategory}
              onSelectCategory={setSelectedCategory}
            />
          </Card>

          {/* Camera button */}
          <Card className="bg-primary-50 border-primary-200">
            <div className="text-center">
              <Camera className="w-12 h-12 text-primary-600 mx-auto mb-3" />
              <h3 className="font-semibold text-slate-900 mb-2">Capture Photo</h3>
              <p className="text-sm text-slate-600 mb-4">
                {selectedCategory
                  ? `Taking photo for: ${selectedCategory.name}`
                  : 'Select a category first'
                }
              </p>
              <Button
                variant="primary"
                icon="Camera"
                onClick={() => setShowCamera(true)}
                disabled={!selectedCategory}
                fullWidth
              >
                Open Camera
              </Button>
            </div>
          </Card>

          {/* Actions */}
          <Card>
            <h3 className="font-semibold text-slate-900 mb-4">Actions</h3>
            <div className="space-y-3">
              {currentSurvey.status === 'in-progress' && currentPhotos.length > 0 && (
                <Button
                  variant="secondary"
                  icon="CheckCircle"
                  onClick={() => setShowCompleteConfirm(true)}
                  fullWidth
                >
                  Mark as Complete
                </Button>
              )}

              {(currentSurvey.status === 'completed' || currentPhotos.length > 0) && (
                <AnalyseButton
                  surveyId={currentSurvey.id}
                  onAnalysisComplete={handleAnalysisComplete}
                />
              )}

              {currentSurvey.status === 'analysed' && (
                <Button
                  variant="outline"
                  icon="FileText"
                  onClick={() => navigate(`/survey/${id}/analysis`)}
                  fullWidth
                >
                  View Analysis
                </Button>
              )}

              <Button
                variant="primary"
                icon="FileText"
                onClick={() => navigate(`/survey/${id}/review`)}
                fullWidth
              >
                Review & Finalize
              </Button>

              <Button
                variant="outline"
                icon="FileDown"
                onClick={handleDownloadDocument}
                fullWidth
                loading={downloading}
              >
                {downloading ? 'Downloading...' : 'Quick Download (Draft)'}
              </Button>

              <hr className="my-4" />

              <Button
                variant="ghost"
                icon="Trash2"
                onClick={() => setShowDeleteConfirm(true)}
                fullWidth
                className="text-red-600 hover:bg-red-50"
              >
                Delete Survey
              </Button>
            </div>
          </Card>
        </div>

        {/* Right column - Photo gallery (scrollable) */}
        <div className="lg:col-span-2 overflow-y-auto">
          <Card>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-slate-900">
                Photos ({currentPhotos.length})
              </h3>
              {selectedCategory && (
                <button
                  onClick={() => setSelectedCategory(null)}
                  className="text-sm text-primary-600 hover:text-primary-700"
                >
                  Show all categories
                </button>
              )}
            </div>
            <PhotoGallery
              photos={currentPhotos}
              onDeletePhoto={deletePhoto}
              onUpdatePhotoNotes={updatePhotoNotes}
              onTogglePhotoAnalyse={togglePhotoAnalyse}
              onReanalyseCategory={handleReanalyseCategory}
              analysisResults={currentSurvey.analysisResult}
              filterCategory={selectedCategory}
              groupByCategory={!selectedCategory}
            />
          </Card>
        </div>
      </div>

      {/* Camera Modal */}
      <Modal
        isOpen={showCamera}
        onClose={() => setShowCamera(false)}
        size="lg"
        showCloseButton={false}
      >
        <CameraCapture
          selectedCategory={selectedCategory}
          onCapture={handleCapture}
          onCancel={() => setShowCamera(false)}
        />
      </Modal>

      {/* Delete Confirmation */}
      <ConfirmModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDeleteSurvey}
        title="Delete Survey"
        message={`Are you sure you want to delete "${currentSurvey.propertyName}"? This will also delete all ${currentPhotos.length} photos. This action cannot be undone.`}
        confirmText="Delete Survey"
        variant="danger"
      />

      {/* Complete Confirmation */}
      <ConfirmModal
        isOpen={showCompleteConfirm}
        onClose={() => setShowCompleteConfirm(false)}
        onConfirm={handleCompleteSurvey}
        title="Complete Survey"
        message="Mark this survey as complete? You can still add more photos and make changes after completing."
        confirmText="Mark Complete"
        variant="primary"
      />
    </div>
  )
}
